/**
 * YAML Codec for Project serialization/deserialization
 *
 * Converts between Project objects and YAML text.
 */

import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { parseNoteBlock, serializeNotes } from './NoteParser.js';
import type {
  Project,
  Track,
  Clip,
  Instrument,
  SDSPInstrument,
  Sample,
  InlineSample,
  SymbolicSample,
  AutomationLane,
  GlobalAutomation,
  EchoAutomationPoint,
  TimeSignature,
  VelocityMap,
} from './types.js';
import { beats, asBeats, isSymbolicSample, parseSymbolicRef, formatSymbolicRef } from './types.js';

// =============================================================================
// Raw YAML Types (what we read/write to YAML)
// =============================================================================

interface RawProject {
  project: {
    name: string;
    tempo: number;
    timeSignature: [number, number];
  };
  samples?: RawSample[];
  instruments?: RawInstrument[];
  tracks?: RawTrack[];
  globalAutomation?: RawGlobalAutomation;
}

interface RawSample {
  id: string;
  brr?: string; // Base64-encoded BRR data (for inline samples)
  ref?: string; // Symbolic reference like "$a88[15]" (for referenced samples)
  loopStart?: number;
  baseNote?: number; // Optional - auto-detected from sample if not specified
  sampleRate?: number;
}

interface RawInstrument {
  id: string;
  type: string;
  sample: string;
  adsr?: [number, number, number, number];
  gainMode?: {
    mode: string;
    rate: number;
    value?: number;
  };
  velocityMap?: string;
  volumeL?: number;
  volumeR?: number;
}

interface RawTrack {
  id: string;
  name: string;
  instrument: string;
  clips?: RawClip[];
  automation?: RawAutomationLane[];
  mute?: boolean;
  solo?: boolean;
}

interface RawClip {
  id: string;
  start: number;
  duration: number;
  notes: string; // Note DSL block
}

interface RawAutomationLane {
  parameter: string;
  points: RawAutomationPoint[];
}

interface RawAutomationPoint {
  time: number;
  value: number;
}

interface RawGlobalAutomation {
  echo?: RawEchoAutomationPoint[];
  masterVolume?: RawAutomationPoint[];
}

interface RawEchoAutomationPoint {
  time: number;
  delay?: number | string; // "120ms" or 120
  feedback?: number;
  volumeL?: number;
  volumeR?: number;
  fir?: number[];
}

// =============================================================================
// Parsing (YAML → Project)
// =============================================================================

/**
 * Parse a YAML string into a Project.
 *
 * @param yaml - YAML string
 * @returns Parsed Project
 * @throws Error if YAML is invalid
 */
export function parseProject(yaml: string): Project {
  const raw = parseYaml(yaml) as RawProject;

  if (!raw.project) {
    throw new Error('Missing "project" section in YAML');
  }

  const samples = (raw.samples ?? []).map(parseSample);
  const instruments = (raw.instruments ?? []).map(parseInstrument);
  const tracks = (raw.tracks ?? []).map(parseTrack);
  const globalAutomation = parseGlobalAutomation(raw.globalAutomation);

  return {
    name: raw.project.name ?? 'Untitled',
    tempo: raw.project.tempo ?? 120,
    timeSignature: parseTimeSignature(raw.project.timeSignature),
    samples,
    instruments,
    tracks,
    globalAutomation,
  };
}

function parseTimeSignature(ts: [number, number] | undefined): TimeSignature {
  if (!ts || !Array.isArray(ts) || ts.length !== 2) {
    return [4, 4];
  }
  return [ts[0], ts[1]];
}

function parseSample(raw: RawSample): Sample {
  // Check for symbolic reference
  if (raw.ref) {
    const ref = parseSymbolicRef(raw.ref);
    if (!ref) {
      throw new Error(
        `Invalid sample reference format: "${raw.ref}". Expected format: $gameId[index]`
      );
    }
    return {
      id: raw.id,
      ref,
      baseNote: raw.baseNote, // undefined = auto-detect from sample
    } as SymbolicSample;
  }

  // Inline sample with BRR data (empty string is valid for placeholder samples)
  if (raw.brr === undefined) {
    throw new Error(`Sample "${raw.id}" must have either "brr" data or a "ref" reference`);
  }

  return {
    id: raw.id,
    brr: raw.brr,
    loopStart: raw.loopStart,
    baseNote: raw.baseNote ?? 60,
    sampleRate: raw.sampleRate ?? 32000,
  } as InlineSample;
}

function parseInstrument(raw: RawInstrument): Instrument {
  if (raw.type !== 'sdsp') {
    throw new Error(`Unknown instrument type: ${raw.type}`);
  }

  const inst: SDSPInstrument = {
    id: raw.id,
    type: 'sdsp',
    sampleId: raw.sample,
    adsr: raw.adsr ?? [15, 7, 7, 20],
    velocityMap: (raw.velocityMap as VelocityMap) ?? 'volume',
  };

  if (raw.gainMode) {
    return {
      ...inst,
      gainMode: {
        mode: raw.gainMode.mode as SDSPInstrument['gainMode'] extends undefined
          ? never
          : NonNullable<SDSPInstrument['gainMode']>['mode'],
        rate: raw.gainMode.rate,
        value: raw.gainMode.value,
      },
    };
  }

  if (raw.volumeL !== undefined || raw.volumeR !== undefined) {
    return {
      ...inst,
      volumeL: raw.volumeL,
      volumeR: raw.volumeR,
    };
  }

  return inst;
}

function parseTrack(raw: RawTrack): Track {
  return {
    id: raw.id,
    name: raw.name,
    instrumentId: raw.instrument,
    clips: (raw.clips ?? []).map(parseClip),
    automation: (raw.automation ?? []).map(parseAutomationLane),
    mute: raw.mute ?? false,
    solo: raw.solo ?? false,
  };
}

function parseClip(raw: RawClip): Clip {
  // Parse notes from DSL block
  const notes = raw.notes ? parseNoteBlock(raw.notes) : [];

  return {
    id: raw.id,
    start: beats(raw.start),
    duration: beats(raw.duration),
    notes,
  };
}

function parseAutomationLane(raw: RawAutomationLane): AutomationLane {
  return {
    parameter: raw.parameter,
    points: raw.points.map((p) => ({
      time: beats(p.time),
      value: p.value,
    })),
  };
}

function parseGlobalAutomation(raw: RawGlobalAutomation | undefined): GlobalAutomation {
  if (!raw) {
    return {};
  }

  return {
    echo: raw.echo?.map(parseEchoPoint),
    masterVolume: raw.masterVolume?.map((p) => ({ time: beats(p.time), value: p.value })),
  };
}

function parseEchoPoint(raw: RawEchoAutomationPoint): EchoAutomationPoint {
  // Parse delay which can be "120ms" or 120
  let delay: number | undefined;
  if (typeof raw.delay === 'string') {
    const match = raw.delay.match(/^(\d+(?:\.\d+)?)\s*ms$/i);
    if (match) {
      delay = parseFloat(match[1]);
    }
  } else {
    delay = raw.delay;
  }

  return {
    time: beats(raw.time),
    delay,
    feedback: raw.feedback,
    volumeL: raw.volumeL,
    volumeR: raw.volumeR,
    fir: raw.fir,
  };
}

// =============================================================================
// Serialization (Project → YAML)
// =============================================================================

/**
 * Serialize a Project to YAML string.
 *
 * @param project - Project to serialize
 * @returns YAML string
 */
export function serializeProject(project: Project): string {
  const raw: RawProject = {
    project: {
      name: project.name,
      tempo: project.tempo,
      timeSignature: [...project.timeSignature] as [number, number],
    },
  };

  if (project.samples.length > 0) {
    raw.samples = project.samples.map(serializeSample);
  }

  if (project.instruments.length > 0) {
    raw.instruments = project.instruments.map(serializeInstrument);
  }

  if (project.tracks.length > 0) {
    raw.tracks = project.tracks.map(serializeTrack);
  }

  const globalAuto = serializeGlobalAutomation(project.globalAutomation);
  if (globalAuto) {
    raw.globalAutomation = globalAuto;
  }

  return stringifyYaml(raw, {
    lineWidth: 0, // Don't wrap lines
    defaultStringType: 'QUOTE_DOUBLE',
    defaultKeyType: 'PLAIN',
  });
}

function serializeSample(sample: Sample): RawSample {
  if (isSymbolicSample(sample)) {
    return {
      id: sample.id,
      ref: formatSymbolicRef(sample.ref),
      baseNote: sample.baseNote,
    };
  }

  // Inline sample
  const raw: RawSample = {
    id: sample.id,
    brr: sample.brr,
    baseNote: sample.baseNote,
  };

  if (sample.loopStart !== undefined) {
    raw.loopStart = sample.loopStart;
  }

  if (sample.sampleRate !== undefined && sample.sampleRate !== 32000) {
    raw.sampleRate = sample.sampleRate;
  }

  return raw;
}

function serializeInstrument(inst: Instrument): RawInstrument {
  const raw: RawInstrument = {
    id: inst.id,
    type: inst.type,
    sample: inst.sampleId,
    adsr: [...inst.adsr] as [number, number, number, number],
    velocityMap: inst.velocityMap,
  };

  if (inst.gainMode) {
    raw.gainMode = {
      mode: inst.gainMode.mode,
      rate: inst.gainMode.rate,
    };
    if (inst.gainMode.value !== undefined) {
      raw.gainMode.value = inst.gainMode.value;
    }
  }

  if (inst.volumeL !== undefined) {
    raw.volumeL = inst.volumeL;
  }
  if (inst.volumeR !== undefined) {
    raw.volumeR = inst.volumeR;
  }

  return raw;
}

function serializeTrack(track: Track): RawTrack {
  const raw: RawTrack = {
    id: track.id,
    name: track.name,
    instrument: track.instrumentId,
  };

  if (track.clips.length > 0) {
    raw.clips = track.clips.map(serializeClip);
  }

  if (track.automation.length > 0) {
    raw.automation = track.automation.map(serializeAutomationLane);
  }

  if (track.mute) {
    raw.mute = true;
  }

  if (track.solo) {
    raw.solo = true;
  }

  return raw;
}

function serializeClip(clip: Clip): RawClip {
  return {
    id: clip.id,
    start: asBeats(clip.start),
    duration: asBeats(clip.duration),
    notes: serializeNotes(clip.notes, 4),
  };
}

function serializeAutomationLane(lane: AutomationLane): RawAutomationLane {
  return {
    parameter: lane.parameter,
    points: lane.points.map((p) => ({ time: asBeats(p.time), value: p.value })),
  };
}

function serializeGlobalAutomation(ga: GlobalAutomation): RawGlobalAutomation | undefined {
  const hasEcho = ga.echo && ga.echo.length > 0;
  const hasMaster = ga.masterVolume && ga.masterVolume.length > 0;

  if (!hasEcho && !hasMaster) {
    return undefined;
  }

  const raw: RawGlobalAutomation = {};

  if (hasEcho) {
    raw.echo = ga.echo!.map(serializeEchoPoint);
  }

  if (hasMaster) {
    raw.masterVolume = ga.masterVolume!.map((p) => ({ time: asBeats(p.time), value: p.value }));
  }

  return raw;
}

function serializeEchoPoint(point: EchoAutomationPoint): RawEchoAutomationPoint {
  const raw: RawEchoAutomationPoint = {
    time: asBeats(point.time),
  };

  if (point.delay !== undefined) {
    raw.delay = `${point.delay}ms`;
  }

  if (point.feedback !== undefined) {
    raw.feedback = point.feedback;
  }

  if (point.volumeL !== undefined) {
    raw.volumeL = point.volumeL;
  }

  if (point.volumeR !== undefined) {
    raw.volumeR = point.volumeR;
  }

  if (point.fir !== undefined) {
    raw.fir = [...point.fir];
  }

  return raw;
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Validate a project for internal consistency.
 *
 * @param project - Project to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateProject(project: Project): string[] {
  const errors: string[] = [];

  // Check all track instruments exist
  const instrumentIds = new Set(project.instruments.map((i) => i.id));
  for (const track of project.tracks) {
    if (!instrumentIds.has(track.instrumentId)) {
      errors.push(`Track "${track.name}" references unknown instrument "${track.instrumentId}"`);
    }
  }

  // Check all instrument samples exist
  const sampleIds = new Set(project.samples.map((s) => s.id));
  for (const inst of project.instruments) {
    if (!sampleIds.has(inst.sampleId)) {
      errors.push(`Instrument "${inst.id}" references unknown sample "${inst.sampleId}"`);
    }
  }

  // Check for duplicate IDs
  const trackIds = project.tracks.map((t) => t.id);
  const duplicateTracks = trackIds.filter((id, i) => trackIds.indexOf(id) !== i);
  for (const id of duplicateTracks) {
    errors.push(`Duplicate track ID: "${id}"`);
  }

  const clipIds = project.tracks.flatMap((t) => t.clips.map((c) => c.id));
  const duplicateClips = clipIds.filter((id, i) => clipIds.indexOf(id) !== i);
  for (const id of duplicateClips) {
    errors.push(`Duplicate clip ID: "${id}"`);
  }

  return errors;
}
